import { useCallback, useEffect, useState } from 'react';
import { ApiError, apiDelete, apiGet, apiPatch, apiPost } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { Layout } from '../components/Layout.js';
import { Loader2Icon } from '../components/icons.js';

/**
 * Admin: Extractor Templates (Slice 4).
 *
 * Lists every template (draft + active). All authenticated knowledgebase
 * users can BROWSE templates (so the extractor upload picker shows them
 * what's available); only `kb_admin` can create / edit / toggle / soft-
 * delete. The page hides admin actions for non-admin viewers so they
 * see the template library without dead buttons.
 */

const RULES_MAX = 8000;
const ROWS_PROMPT_MAX = 16000;
const HEADER_PROMPT_MAX = 8000;
const ROWS_PROMPT_MIN = 100;
const LEGACY_SHAPE = 'GncWanBlFl';

type ColumnType = 'string' | 'number' | 'date' | 'enum';

interface ExtractorColumn {
  name: string;
  type: ColumnType;
  required: boolean;
  isKey?: boolean;
  isReconcileNumeric?: boolean;
  notes?: string;
  enumValues?: string[];
}

interface ExtractorTemplate {
  id: string;
  name: string;
  displayName: string;
  format: string;
  outputShape: string;
  vendor: string;
  accessProvider: string;
  headerPrompt?: string;
  rowsPrompt: string;
  columns?: ExtractorColumn[];
  extractionRules: string;
  status: 'draft' | 'active';
  version: number;
  createdBy: string;
  createdAt: string;
  updatedAt?: string;
}

interface EditDraft {
  name: string;
  displayName: string;
  outputShape: string;
  vendor: string;
  accessProvider: string;
  rowsPrompt: string;
  headerPrompt: string;
  extractionRules: string;
  columns: ExtractorColumn[];
}

function emptyDraft(): EditDraft {
  return {
    name: '',
    displayName: '',
    outputShape: LEGACY_SHAPE,
    vendor: '',
    accessProvider: 'BCX',
    rowsPrompt: '',
    headerPrompt: '',
    extractionRules: '',
    columns: [],
  };
}

function emptyColumn(): ExtractorColumn {
  return { name: '', type: 'string', required: false };
}

function statusChip(status: ExtractorTemplate['status']): JSX.Element {
  if (status === 'active') {
    return (
      <span className="ov-meta-upper inline-flex items-center rounded-md bg-nebula-success-bg px-2 py-0.5 text-nebula-success">
        Active
      </span>
    );
  }
  return (
    <span className="ov-meta-upper inline-flex items-center rounded-md bg-nebula-gray-5 px-2 py-0.5 text-nebula-fg-2">
      Draft
    </span>
  );
}

export function AdminExtractorTemplatesPage(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'kb_admin';
  const [templates, setTemplates] = useState<ExtractorTemplate[]>([]);
  const [distinctVendors, setDistinctVendors] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState<EditDraft>(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const res = await apiGet<{ templates: ExtractorTemplate[]; distinctVendors?: string[] }>(
        '/api/v1/extractor/templates',
      );
      setTemplates(res.templates ?? []);
      setDistinctVendors(res.distinctVendors ?? []);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load templates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const beginCreate = (): void => {
    setEditingId(null);
    setDraft(emptyDraft());
    setCreateOpen(true);
  };
  const beginEdit = (t: ExtractorTemplate): void => {
    setEditingId(t.id);
    setDraft({
      name: t.name,
      displayName: t.displayName,
      outputShape: t.outputShape ?? t.format ?? LEGACY_SHAPE,
      vendor: t.vendor ?? '',
      accessProvider: t.accessProvider ?? '',
      rowsPrompt: t.rowsPrompt ?? '',
      headerPrompt: t.headerPrompt ?? '',
      extractionRules: t.extractionRules ?? '',
      columns: (t.columns ?? []).map((c) => ({ ...c })),
    });
    setCreateOpen(true);
  };
  const cancelEdit = (): void => {
    setCreateOpen(false);
    setEditingId(null);
    setDraft(emptyDraft());
  };

  const submit = async (): Promise<void> => {
    if (!draft.name.trim() || !draft.displayName.trim()) {
      setError('Name and display name are required.');
      return;
    }
    if (!draft.vendor.trim()) {
      setError('Vendor is required.');
      return;
    }
    if (!draft.accessProvider.trim()) {
      setError('Access provider is required.');
      return;
    }
    if (!/^[a-zA-Z][a-zA-Z0-9_-]*$/.test(draft.outputShape)) {
      setError('Output shape must be a slug (letters/digits/dash/underscore, leading letter).');
      return;
    }
    if (draft.rowsPrompt.length < ROWS_PROMPT_MIN) {
      setError(`Rows prompt must be at least ${ROWS_PROMPT_MIN} characters.`);
      return;
    }
    if (draft.rowsPrompt.length > ROWS_PROMPT_MAX) {
      setError(`Rows prompt exceeds ${ROWS_PROMPT_MAX} characters.`);
      return;
    }
    if (draft.headerPrompt.length > HEADER_PROMPT_MAX) {
      setError(`Header prompt exceeds ${HEADER_PROMPT_MAX} characters.`);
      return;
    }
    if (draft.extractionRules.length > RULES_MAX) {
      setError(`Extraction rules exceed ${RULES_MAX} chars.`);
      return;
    }
    if (draft.columns.length === 0 && draft.outputShape !== LEGACY_SHAPE) {
      setError('Non-legacy output shapes require at least one column.');
      return;
    }
    const seen = new Set<string>();
    for (const c of draft.columns) {
      if (!c.name.trim()) {
        setError('Every column needs a name.');
        return;
      }
      const k = c.name.trim().toLowerCase();
      if (seen.has(k)) {
        setError(`Duplicate column name: ${c.name}.`);
        return;
      }
      seen.add(k);
      if (c.isReconcileNumeric && c.type !== 'number') {
        setError(`Column "${c.name}" is marked reconcile-numeric but type is not 'number'.`);
        return;
      }
    }
    if (draft.columns.filter((c) => c.isKey).length > 1) {
      setError('At most one column may be marked as the key column.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: draft.name.trim(),
        displayName: draft.displayName.trim(),
        vendor: draft.vendor.trim(),
        accessProvider: draft.accessProvider.trim(),
        outputShape: draft.outputShape.trim(),
        rowsPrompt: draft.rowsPrompt,
        ...(draft.headerPrompt.trim() ? { headerPrompt: draft.headerPrompt } : {}),
        extractionRules: draft.extractionRules,
        columns: draft.columns.map((c) => ({
          name: c.name.trim(),
          type: c.type,
          required: !!c.required,
          ...(c.isKey ? { isKey: true } : {}),
          ...(c.isReconcileNumeric ? { isReconcileNumeric: true } : {}),
          ...(c.notes?.trim() ? { notes: c.notes.trim() } : {}),
          ...(c.type === 'enum' && c.enumValues?.length
            ? { enumValues: c.enumValues.map((v) => v.trim()).filter(Boolean) }
            : {}),
        })),
      };
      if (editingId) {
        await apiPatch(`/api/v1/extractor/templates/${editingId}`, payload);
      } else {
        await apiPost('/api/v1/extractor/templates', payload);
      }
      cancelEdit();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (t: ExtractorTemplate): Promise<void> => {
    setBusyId(t.id);
    setError(null);
    try {
      if (t.status === 'active') {
        await apiPost(`/api/v1/extractor/templates/${t.id}/deactivate`, {});
      } else {
        await apiPost(`/api/v1/extractor/templates/${t.id}/activate`, { status: 'active' });
      }
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change status.');
    } finally {
      setBusyId(null);
    }
  };

  const softDelete = async (t: ExtractorTemplate): Promise<void> => {
    if (
      !window.confirm(
        `Move "${t.displayName}" to draft? Runs reference templateVersion, so existing history is preserved.`,
      )
    ) {
      return;
    }
    setBusyId(t.id);
    setError(null);
    try {
      await apiDelete(`/api/v1/extractor/templates/${t.id}`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete.');
    } finally {
      setBusyId(null);
    }
  };

  const updateColumn = (idx: number, patch: Partial<ExtractorColumn>): void => {
    setDraft((d) => {
      const next = d.columns.slice();
      const cur = next[idx];
      if (!cur) return d;
      next[idx] = { ...cur, ...patch };
      return { ...d, columns: next };
    });
  };

  const removeColumn = (idx: number): void => {
    setDraft((d) => ({ ...d, columns: d.columns.filter((_, i) => i !== idx) }));
  };

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-6 py-8">
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="ov-h-page">Extractor Templates</h1>
            <p className="ov-body mt-1 max-w-3xl text-nebula-fg-3">
              The shared library of import-format templates the Data Extractor runs against. Only
              active templates appear in the upload wizard. Editing rules bumps the version; runs
              snapshot the version they were launched against, so older outputs stay reproducible.
            </p>
          </div>
          {isAdmin && (
            <div className="flex shrink-0 items-center gap-3">
              <a
                href="/admin/template-builder"
                className="rounded-md border border-nebula-border px-4 py-2 text-sm font-medium text-nebula-secondary hover:bg-nebula-tint"
              >
                Build from example (AI)
              </a>
              <button
                type="button"
                onClick={beginCreate}
                className="rounded-md bg-nebula-primary px-4 py-2 text-sm font-medium text-white hover:bg-nebula-secondary"
              >
                New template
              </button>
            </div>
          )}
        </header>

        {error && (
          <div className="mb-4 rounded-md border border-nebula-error/30 bg-nebula-error-bg px-3 py-2 text-sm text-nebula-error">
            {error}
          </div>
        )}

        {createOpen && (
          <section className="mb-6 rounded-lg border border-nebula-border-soft bg-nebula-surface p-4">
            <h2 className="ov-h-section mb-3">{editingId ? 'Edit template' : 'Create template'}</h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="text-sm">
                <span className="ov-meta-upper mb-1 block text-nebula-fg-3">Name</span>
                <input
                  type="text"
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  maxLength={120}
                  className="w-full rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 text-sm focus:border-nebula-secondary focus:outline-none"
                  placeholder="imacd-bcx-coc"
                />
              </label>
              <label className="text-sm">
                <span className="ov-meta-upper mb-1 block text-nebula-fg-3">Display name</span>
                <input
                  type="text"
                  value={draft.displayName}
                  onChange={(e) => setDraft((d) => ({ ...d, displayName: e.target.value }))}
                  maxLength={200}
                  className="w-full rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 text-sm focus:border-nebula-secondary focus:outline-none"
                  placeholder="IMACD — BCX Certificate of Completion"
                />
              </label>
              <label className="text-sm">
                <span className="ov-meta-upper mb-1 block text-nebula-fg-3">Vendor</span>
                <input
                  type="text"
                  list="extractor-vendor-options"
                  value={draft.vendor}
                  onChange={(e) => setDraft((d) => ({ ...d, vendor: e.target.value.slice(0, 80) }))}
                  maxLength={80}
                  className="w-full rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 text-sm focus:border-nebula-secondary focus:outline-none"
                  placeholder="Type a new vendor or pick existing"
                />
                <datalist id="extractor-vendor-options">
                  {distinctVendors.map((v) => (
                    <option key={v} value={v} />
                  ))}
                </datalist>
                <p className="ov-meta mt-1 text-nebula-fg-4">
                  Free text. Existing vendors auto-suggest; type to create a new one.
                </p>
              </label>
              <label className="text-sm">
                <span className="ov-meta-upper mb-1 block text-nebula-fg-3">Access provider</span>
                <input
                  type="text"
                  value={draft.accessProvider}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, accessProvider: e.target.value.slice(0, 60) }))
                  }
                  maxLength={60}
                  className="w-full rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 text-sm focus:border-nebula-secondary focus:outline-none"
                  placeholder="BCX"
                />
              </label>
              <label className="text-sm md:col-span-2">
                <span className="ov-meta-upper mb-1 block text-nebula-fg-3">Output shape</span>
                <input
                  type="text"
                  value={draft.outputShape}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, outputShape: e.target.value.slice(0, 80) }))
                  }
                  disabled={!!editingId}
                  maxLength={80}
                  className="w-full rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 font-mono text-[12px] focus:border-nebula-secondary focus:outline-none disabled:opacity-60"
                  placeholder="ImacdBcxCoc"
                />
                <p className="ov-meta mt-1 text-nebula-fg-4">
                  Slug (letters/digits/dash/underscore). <code>GncWanBlFl</code> is the back-compat
                  shape with the hardcoded 36-col mapper; every other shape needs columns defined
                  below.
                </p>
              </label>

              {/* Column editor */}
              <fieldset className="text-sm md:col-span-2">
                <legend className="ov-meta-upper mb-2 text-nebula-fg-3">
                  Columns ({draft.columns.length})
                </legend>
                {draft.columns.length === 0 ? (
                  <p className="ov-meta mb-2 text-nebula-fg-4">
                    No columns. Add at least one for non-legacy shapes; the legacy{' '}
                    <code>GncWanBlFl</code> shape can leave this empty.
                  </p>
                ) : (
                  <div className="mb-2 overflow-hidden rounded-md border border-nebula-border-soft">
                    <table className="w-full text-xs">
                      <thead className="bg-nebula-gray-6">
                        <tr>
                          <th className="ov-meta-upper px-2 py-1.5 text-left">Name</th>
                          <th className="ov-meta-upper px-2 py-1.5 text-left">Type</th>
                          <th className="ov-meta-upper px-2 py-1.5 text-center">Required</th>
                          <th className="ov-meta-upper px-2 py-1.5 text-center">Key</th>
                          <th className="ov-meta-upper px-2 py-1.5 text-center">Recon $</th>
                          <th className="ov-meta-upper px-2 py-1.5 text-left">Notes</th>
                          <th className="ov-meta-upper px-2 py-1.5 text-right"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {draft.columns.map((c, idx) => (
                          <tr key={idx} className="border-t border-nebula-border-soft">
                            <td className="px-2 py-1">
                              <input
                                type="text"
                                value={c.name}
                                onChange={(e) => updateColumn(idx, { name: e.target.value })}
                                maxLength={120}
                                className="w-full rounded border border-nebula-border bg-nebula-surface px-2 py-1 font-mono text-[11px]"
                                placeholder="SiteCode"
                              />
                            </td>
                            <td className="px-2 py-1">
                              <select
                                value={c.type}
                                onChange={(e) =>
                                  updateColumn(idx, { type: e.target.value as ColumnType })
                                }
                                className="w-full rounded border border-nebula-border bg-nebula-surface px-1 py-1 text-[11px]"
                              >
                                <option value="string">string</option>
                                <option value="number">number</option>
                                <option value="date">date</option>
                                <option value="enum">enum</option>
                              </select>
                            </td>
                            <td className="px-2 py-1 text-center">
                              <input
                                type="checkbox"
                                checked={!!c.required}
                                onChange={(e) => updateColumn(idx, { required: e.target.checked })}
                              />
                            </td>
                            <td className="px-2 py-1 text-center">
                              <input
                                type="checkbox"
                                checked={!!c.isKey}
                                onChange={(e) =>
                                  updateColumn(idx, { isKey: e.target.checked || undefined })
                                }
                              />
                            </td>
                            <td className="px-2 py-1 text-center">
                              <input
                                type="checkbox"
                                checked={!!c.isReconcileNumeric}
                                disabled={c.type !== 'number'}
                                onChange={(e) =>
                                  updateColumn(idx, {
                                    isReconcileNumeric: e.target.checked || undefined,
                                  })
                                }
                              />
                            </td>
                            <td className="px-2 py-1">
                              <input
                                type="text"
                                value={c.notes ?? ''}
                                onChange={(e) =>
                                  updateColumn(idx, { notes: e.target.value || undefined })
                                }
                                maxLength={500}
                                className="w-full rounded border border-nebula-border bg-nebula-surface px-2 py-1 text-[11px]"
                                placeholder="e.g. printed as 'Site Number'"
                              />
                            </td>
                            <td className="px-2 py-1 text-right">
                              <button
                                type="button"
                                onClick={() => removeColumn(idx)}
                                className="rounded border border-nebula-border bg-nebula-surface px-2 py-0.5 text-[11px] text-nebula-error hover:bg-nebula-error-bg"
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() =>
                    setDraft((d) => ({ ...d, columns: [...d.columns, emptyColumn()] }))
                  }
                  className="rounded-md border border-nebula-border bg-nebula-surface px-3 py-1 text-xs text-nebula-fg-1 hover:bg-nebula-tint"
                >
                  + Add column
                </button>
                <p className="ov-meta mt-2 text-nebula-fg-4">
                  <b>Key</b>: the dedup/identity column (at most one). <b>Recon $</b>: sum into the
                  reconciliation total (numeric columns only).
                </p>
              </fieldset>

              <label className="text-sm md:col-span-2">
                <span className="ov-meta-upper mb-1 block text-nebula-fg-3">
                  Rows prompt (Vision rows-pass)
                </span>
                <textarea
                  value={draft.rowsPrompt}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      rowsPrompt: e.target.value.slice(0, ROWS_PROMPT_MAX),
                    }))
                  }
                  rows={14}
                  className="w-full resize-y rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 font-mono text-[12px] focus:border-nebula-secondary focus:outline-none"
                  placeholder="Extract every key-value pair from this IMACD certificate…"
                />
                <p
                  className={`ov-meta mt-1 text-right ${
                    draft.rowsPrompt.length >= ROWS_PROMPT_MAX ||
                    draft.rowsPrompt.length < ROWS_PROMPT_MIN
                      ? 'text-nebula-error'
                      : 'text-nebula-fg-4'
                  }`}
                >
                  {draft.rowsPrompt.length} / {ROWS_PROMPT_MAX}
                  {draft.rowsPrompt.length < ROWS_PROMPT_MIN ? ` (min ${ROWS_PROMPT_MIN})` : ''}
                </p>
              </label>
              <details className="text-sm md:col-span-2">
                <summary className="ov-meta-upper cursor-pointer text-nebula-fg-3">
                  Advanced: header prompt override (optional)
                </summary>
                <textarea
                  value={draft.headerPrompt}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      headerPrompt: e.target.value.slice(0, HEADER_PROMPT_MAX),
                    }))
                  }
                  rows={6}
                  className="mt-2 w-full resize-y rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 font-mono text-[12px] focus:border-nebula-secondary focus:outline-none"
                  placeholder="Leave empty to use the shared default header prompt."
                />
                <p className="ov-meta mt-1 text-right text-nebula-fg-4">
                  {draft.headerPrompt.length} / {HEADER_PROMPT_MAX}
                </p>
              </details>
              <label className="text-sm md:col-span-2">
                <span className="ov-meta-upper mb-1 block text-nebula-fg-3">Operator notes</span>
                <textarea
                  value={draft.extractionRules}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      extractionRules: e.target.value.slice(0, RULES_MAX),
                    }))
                  }
                  rows={4}
                  className="w-full resize-y rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 font-mono text-[12px] focus:border-nebula-secondary focus:outline-none"
                  placeholder="Free-text notes for the next admin (not used by the extractor)."
                />
                <p
                  className={`ov-meta mt-1 text-right ${
                    draft.extractionRules.length >= RULES_MAX
                      ? 'text-nebula-error'
                      : 'text-nebula-fg-4'
                  }`}
                >
                  {draft.extractionRules.length} / {RULES_MAX}
                </p>
              </label>
            </div>
            <div className="mt-3 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={cancelEdit}
                disabled={saving}
                className="rounded-md border border-nebula-border bg-nebula-surface px-3 py-1.5 text-sm text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-md bg-nebula-primary px-4 py-1.5 text-sm font-medium text-white hover:bg-nebula-secondary disabled:opacity-50"
              >
                {saving && <Loader2Icon size={14} className="animate-spin" />}
                {editingId ? 'Save changes' : 'Create template'}
              </button>
            </div>
          </section>
        )}

        {loading ? (
          <p className="inline-flex items-center gap-2 text-sm text-nebula-fg-3">
            <Loader2Icon size={14} className="animate-spin" />
            Loading templates…
          </p>
        ) : templates.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-nebula-border bg-nebula-surface p-10 text-center text-sm text-nebula-fg-3">
            No templates yet.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-nebula-border-soft bg-nebula-surface">
            {/* overflow-x-auto, not hidden — at 1280px the Actions column was
                clipped off the card edge with no way to reach the buttons. */}
            <table className="w-full min-w-[64rem] text-sm">
              <thead className="border-b border-nebula-border-soft bg-nebula-gray-6">
                <tr>
                  <th className="ov-meta-upper px-3 py-2 text-left">Display name</th>
                  <th className="ov-meta-upper px-3 py-2 text-left">Vendor</th>
                  <th className="ov-meta-upper px-3 py-2 text-left">Output shape</th>
                  <th className="ov-meta-upper px-3 py-2 text-right">Cols</th>
                  <th className="ov-meta-upper px-3 py-2 text-left">Status</th>
                  <th className="ov-meta-upper px-3 py-2 text-right">Version</th>
                  <th className="ov-meta-upper px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => {
                  const isVendorActive =
                    t.status === 'active' &&
                    t.vendor &&
                    t.vendor !== 'unknown' &&
                    templates.find(
                      (other) =>
                        other.id !== t.id &&
                        other.vendor === t.vendor &&
                        other.outputShape === t.outputShape &&
                        other.status === 'active',
                    ) === undefined;
                  return (
                    <tr
                      key={t.id}
                      className="border-t border-nebula-border-soft hover:bg-nebula-tint"
                    >
                      <td className="px-3 py-2 align-top text-nebula-fg-1">
                        <div>{t.displayName}</div>
                        <div className="ov-meta font-mono text-[11px] text-nebula-fg-3">
                          {t.name}
                        </div>
                      </td>
                      {/* Vendor cell says the vendor ONCE. The "vendor default"
                          badge lives with STATUS (it's status information), and
                          the provider line only appears when it differs from
                          the vendor — before this the same value could render
                          three times in one cell. */}
                      <td className="px-3 py-2 align-top text-nebula-fg-1">
                        <span>{t.vendor ?? '—'}</span>
                        {t.accessProvider && t.accessProvider !== t.vendor && (
                          <div className="ov-meta text-[11px] text-nebula-fg-3">
                            provider: {t.accessProvider}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top font-mono text-[12px] text-nebula-fg-2">
                        {t.outputShape ?? t.format}
                      </td>
                      <td className="px-3 py-2 text-right align-top font-mono text-[12px] text-nebula-fg-2">
                        {(t.columns ?? []).length || (t.outputShape === LEGACY_SHAPE ? '36*' : '0')}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {statusChip(t.status)}
                        {isVendorActive && (
                          <span
                            title={`The template runs use when a ${t.vendor} file is uploaded without an explicit pick.`}
                            className="mt-1 block text-[10px] text-nebula-fg-3"
                          >
                            vendor default
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right align-top font-mono text-[12px] text-nebula-fg-2">
                        v{t.version}
                      </td>
                      <td className="px-3 py-2 text-right align-top">
                        {isAdmin ? (
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => beginEdit(t)}
                              disabled={busyId === t.id}
                              className="rounded border border-nebula-border bg-nebula-surface px-2 py-1 text-xs text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-40"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => void toggleStatus(t)}
                              disabled={busyId === t.id}
                              className="rounded border border-nebula-border bg-nebula-surface px-2 py-1 text-xs text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-40"
                            >
                              {t.status === 'active' ? 'Move to draft' : 'Activate'}
                            </button>
                            <button
                              type="button"
                              onClick={() => void softDelete(t)}
                              disabled={busyId === t.id}
                              className="rounded border border-nebula-border bg-nebula-surface px-2 py-1 text-xs text-nebula-error hover:bg-nebula-error-bg disabled:opacity-40"
                            >
                              Delete
                            </button>
                          </div>
                        ) : (
                          <span className="ov-meta text-[11px] text-nebula-fg-4">Read-only</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
